import {
  timeToMinutes,
  timeToMinutesWithSeconds,
  calculateSegmentLength
} from './utils';
import {
  WeatherType,
  TimeInterval,
  IntervalConfig,
  WeatherRuleMap,
  GetSParams
} from './types';

/**
 * 计算所有关键时间区间的起止分钟
 * @param sunriseTime 日出时间（HH:MM）
 * @param sunsetTime 日落时间（HH:MM）
 * @returns 区间配置表
 */
export const calculateTimeIntervals = (
  sunriseTime: string,
  sunsetTime: string
): Record<TimeInterval, IntervalConfig> => {
  // 转换为总分钟数
  let sunriseMin = timeToMinutes(sunriseTime);
  let sunsetMin = timeToMinutes(sunsetTime);

  // 日落不晚于日出时（含 00:00、极昼等），将日落视为次日同一时刻（+24h）
  if (sunriseMin >= sunsetMin) {
    sunsetMin += 24 * 60;
  }

  // 白天时间 = 日落−日出；晚上的时间 = 24−(日落−日出)
  const dayTotal = sunsetMin - sunriseMin;
  const nightTotal = 24 * 60 - dayTotal;

  // 白天分 12 段 = dayTotal/12；晚上的基础分段仍按 12 段（用于日出前 S2 等），
  // 但 S13/S14 使用更细的 1/36·h（见 final-s-values-table.md）
  const daySegment = calculateSegmentLength(dayTotal);
  const nightSegment12 = calculateSegmentLength(nightTotal);
  const nightSegment36 = nightTotal / 36;

  // 将分钟数统一到“精确到秒”的粒度：四舍五入到最近的秒
  const roundToSeconds = (minutes: number): number =>
    Math.round(minutes * 60) / 60;

  // 将「N 个分段」换算为分钟偏移量
  const segOffset = (segmentMinutes: number, segmentsCount: number): number =>
    segmentMinutes * segmentsCount;

  // 定义所有区间（start/end 为浮点数分钟，判定时左闭右开，且都对齐到秒）
  return {
    night2ToSunrise1: {
      // 从日落时刻开始到日出后第一个 1/12（S5）
      start: sunsetMin,
      end: roundToSeconds(sunriseMin + segOffset(daySegment, 1))
    },
    sunrise1ToSunset1: {
      start: roundToSeconds(sunriseMin + segOffset(daySegment, 1)),
      end: roundToSeconds(sunsetMin - segOffset(daySegment, 1))
    },
    sunset1ToNight2: {
      start: roundToSeconds(sunsetMin - segOffset(daySegment, 1)),
      // 结束边界使用 S14：日落后的第二个 1/36·h
      end: roundToSeconds(sunsetMin + segOffset(nightSegment36, 2))
    },
    night2Only: {
      // 起点同 S14，终点仍按「日出前 2 个 1/12」计算
      start: roundToSeconds(sunsetMin + segOffset(nightSegment36, 2)),
      end: roundToSeconds(
        sunriseMin - segOffset(nightSegment12, 2)
      ) // 日出前 N 个 1/12 按晚上每段算
    },
    dayTotal: {
      start: sunriseMin,
      end: sunsetMin
    },
    nightTotal: {
      start: sunsetMin,
      end: sunriseMin + 24 * 60 // 跨天处理：次日日出
    }
  };
};

/**
 * 判断时间是否在指定区间内（支持跨天）
 * 区间边界与当前时间均支持精确到秒（浮点数分钟），判定为左闭右开 [start, end)。
 * @param currentMinutes 当前时间（分钟，可为小数以精确到秒）
 * @param start 区间开始时间（分钟，可含小数）
 * @param end 区间结束时间（分钟，可含小数）
 * @returns 是否在区间内
 */
const isInInterval = (
  currentMinutes: number,
  start: number,
  end: number
): boolean => {
  const DAY_MINUTES = 24 * 60;

  // 非跨天区间：[start, end)，边界精确到秒
  if (start < end) {
    return currentMinutes >= start && currentMinutes < end;
  }

  // 跨天区间：等价于 [start, 24h) ∪ [0, end)，右开
  const eNorm = end % DAY_MINUTES;
  return currentMinutes >= start || currentMinutes < eNorm;
};

/**
 * 判断当前时间所属的区间
 * @param intervals 时间区间配置
 * @param currentMinutes 当前时间总分钟数
 * @returns 所属区间
 */
export const getCurrentInterval = (
  intervals: Record<TimeInterval, IntervalConfig>,
  currentMinutes: number
): TimeInterval => {
  // 按优先级匹配精准分段区间
  // 注意：
  // 1. night2Only 需要在 sunset1ToNight2 之前检查，以确保边界点 19:10 匹配到 night2Only
  // 2. sunset1ToNight2 需要在 sunrise1ToSunset1 之前检查，以确保边界点 16:05 匹配到 sunset1ToNight2
  // 3. sunrise1ToSunset1 需要在 night2ToSunrise1 之前检查，以确保边界点 06:55 匹配到 sunrise1ToSunset1
  const priorityIntervals: TimeInterval[] = [
    'night2Only',
    'sunset1ToNight2',
    'sunrise1ToSunset1',
    'night2ToSunrise1'
  ];

  for (const intervalName of priorityIntervals) {
    const interval = intervals[intervalName];
    if (isInInterval(currentMinutes, interval.start, interval.end)) {
      return intervalName;
    }
  }

  // 兜底匹配总区间
  if (
    isInInterval(
      currentMinutes,
      intervals.dayTotal.start,
      intervals.dayTotal.end
    )
  ) {
    return 'dayTotal';
  }

  // 默认返回夜间总区间
  return 'nightTotal';
};

/**
 * 天气规则映射表（核心规则）
 */
export const weatherRuleMap: WeatherRuleMap = {
  [WeatherType.Cloudy]: {
    night2ToSunrise1: 0,
    sunrise1ToSunset1: 1,
    sunset1ToNight2: 2,
    night2Only: 3
  },
  [WeatherType.Sunny]: {
    night2ToSunrise1: 4,
    sunrise1ToSunset1: 5,
    sunset1ToNight2: 6,
    night2Only: 7
  },
  [WeatherType.Fog]: {
    dayTotal: 8,
    nightTotal: 9
  },
  [WeatherType.Overcast]: {
    dayTotal: 10,
    nightTotal: 11
  },
  [WeatherType.Haze]: {
    dayTotal: 12,
    nightTotal: 13
  },
  [WeatherType.LightRain]: {
    dayTotal: 14,
    nightTotal: 15
  },
  [WeatherType.ModerateRain]: {
    dayTotal: 16,
    nightTotal: 17
  },
  [WeatherType.HeavyRain]: {
    dayTotal: 18,
    nightTotal: 19
  },
  [WeatherType.FreezingRain]: {
    dayTotal: 20,
    nightTotal: 21
  },
  [WeatherType.Thundershower]: {
    dayTotal: 22,
    nightTotal: 23
  },
  [WeatherType.Sleet]: {
    dayTotal: 24,
    nightTotal: 25
  },
  [WeatherType.Hail]: {
    dayTotal: 26,
    nightTotal: 27
  },
  [WeatherType.FloatingDust]: {
    dayTotal: 28,
    nightTotal: 29
  },
  [WeatherType.Sandstorm]: {
    dayTotal: 30,
    nightTotal: 31
  },
  [WeatherType.LightSnow]: {
    dayTotal: 32,
    nightTotal: 33
  },
  [WeatherType.ModerateSnow]: {
    dayTotal: 34,
    nightTotal: 35
  },
  [WeatherType.HeavySnow]: {
    dayTotal: 36,
    nightTotal: 37
  },
  [WeatherType.Unknown]: {
    dayTotal: 38,
    nightTotal: 39
  }
};

/**
 * 核心函数：根据参数计算 S 值
 * @param params 入参（天气、日出、日落）
 * @returns 匹配的 S 值，出错时返回 -1
 */
export const getS = ({
  weather,
  sunriseTime,
  sunsetTime,
  currentTime
}: GetSParams): number => {
  try {
    // 1. 校验必填参数
    if (!weather) throw new Error('天气类型为必填参数');
    if (!sunriseTime) throw new Error('日出时间为必填参数');
    if (!sunsetTime) throw new Error('日落时间为必填参数');
    if (currentTime === undefined || currentTime === null) {
      throw new Error('当前时间为必填参数');
    }

    // 2. 计算时间区间
    const intervals = calculateTimeIntervals(sunriseTime, sunsetTime);

    // 3. 获取当前时间（支持 HH:MM 或 HH:MM:SS，精确到秒）并判断所属区间
    const currentMinutes: number =
      typeof currentTime === 'string'
        ? timeToMinutesWithSeconds(currentTime)
        : currentTime;
    const currentInterval = getCurrentInterval(intervals, currentMinutes);

    // 4. 匹配 S 值
    const rule = weatherRuleMap[weather as keyof WeatherRuleMap];
    if (!rule) throw new Error(`未配置 ${weather} 对应的规则`);

    let sValue = rule[currentInterval];

    // 如果精准分段区间没有配置，回退到总区间（dayTotal 或 nightTotal）
    if (sValue === undefined) {
      // 判断是白天还是夜晚
      const isDaytime = isInInterval(
        currentMinutes,
        intervals.dayTotal.start,
        intervals.dayTotal.end
      );
      sValue = isDaytime ? rule.dayTotal : rule.nightTotal;

      // 如果总区间也没有配置，抛出错误
      if (sValue === undefined) {
        throw new Error(`${weather} 在 ${currentInterval} 区间无匹配的 S 值`);
      }
    }

    return sValue;
  } catch (error) {
    // 统一错误处理：打印错误并返回 -1 作为错误标记
    console.error(
      '[getS 错误]',
      error instanceof Error ? error.message : String(error)
    );
    return -1;
  }
};

/**
 * 简化版天色计算函数：只区分白天/夜晚
 * @param params 入参（天气、日出、日落、当前时间）
 * @returns 匹配的 S 值（0-24），出错时返回 -1
 */
export const getSSimple = ({
  weather,
  sunriseTime,
  sunsetTime,
  currentTime
}: GetSParams): number => {
  try {
    // 1. 校验必填参数
    if (!weather) throw new Error('天气类型为必填参数');
    if (!sunriseTime) throw new Error('日出时间为必填参数');
    if (!sunsetTime) throw new Error('日落时间为必填参数');
    if (currentTime === undefined || currentTime === null) {
      throw new Error('当前时间为必填参数');
    }

    // 2. 计算时间区间
    const intervals = calculateTimeIntervals(sunriseTime, sunsetTime);

    // 3. 获取当前时间（支持 HH:MM 或 HH:MM:SS，精确到秒）并判断是白天还是夜晚
    const currentMinutes: number =
      typeof currentTime === 'string'
        ? timeToMinutesWithSeconds(currentTime)
        : currentTime;
    const isDaytime = isInInterval(
      currentMinutes,
      intervals.dayTotal.start,
      intervals.dayTotal.end
    );

    // 4. 根据天气类型和时间段返回对应值
    // 未知天气直接返回 24
    if (weather === WeatherType.Unknown) {
      return 24;
    }

    // 定义天气类型组
    const rainTypes = [
      WeatherType.LightRain,
      WeatherType.ModerateRain,
      WeatherType.HeavyRain,
      WeatherType.FreezingRain
    ];
    const snowTypes = [
      WeatherType.LightSnow,
      WeatherType.ModerateSnow,
      WeatherType.HeavySnow
    ];

    // 简化规则映射表（夜晚值，白天值）；白天为较小 S 值、夜晚为较大 S 值（与 getSSimple-s-values-table 一致）
    const simpleWeatherMap: Record<string, [number, number]> = {
      [WeatherType.Cloudy]: [1, 0],
      [WeatherType.Sunny]: [3, 2],
      [WeatherType.Fog]: [5, 4],
      [WeatherType.Overcast]: [7, 6],
      [WeatherType.Haze]: [9, 8],
      [WeatherType.Thundershower]: [13, 12],
      [WeatherType.Sleet]: [15, 14],
      [WeatherType.Hail]: [17, 16],
      [WeatherType.FloatingDust]: [19, 18],
      [WeatherType.Sandstorm]: [21, 20]
    };

    // 处理雨类天气：白天 10，夜晚 11
    if (rainTypes.includes(weather as WeatherType)) {
      return isDaytime ? 10 : 11;
    }

    // 处理雪类天气：白天 22，夜晚 23
    if (snowTypes.includes(weather as WeatherType)) {
      return isDaytime ? 22 : 23;
    }

    // 处理其他天气类型
    const values = simpleWeatherMap[weather];
    if (!values) {
      throw new Error(`未配置 ${weather} 对应的简化规则`);
    }

    return isDaytime ? values[1] : values[0];
  } catch (error) {
    // 统一错误处理：打印错误并返回 -1 作为错误标记
    console.error(
      '[getSSimple 错误]',
      error instanceof Error ? error.message : String(error)
    );
    return -1;
  }
};
